import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchWorkersByProject, fetchMembersByProject, fetchProject } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useI18n } from "../../i18n";
import SearchIcon from "@mui/icons-material/Search";

export default function BuildProjectMemberListPage() {
  const { businessDomainId, businessUnitId, projectId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { locale } = useI18n();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [project, setProject] = useState(null);

  const basePath = `/build/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`;

  const getMemberTypeLabel = (industryId) =>
    industryId === "9999" ? t("member.type.company") : t("member.type.market");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, workers, members] = await Promise.all([
        fetchProject(projectId).catch(() => null),
        fetchWorkersByProject(projectId).catch(() => []),
        fetchMembersByProject(projectId).catch(() => []),
      ]);
      setProject(proj);
      // memberId をキーにした Map を作成して Worker に industryId を合成
      const memberMap = {};
      (Array.isArray(members) ? members : []).forEach((m) => {
        memberMap[m.memberId] = m;
      });
      setRows(
        (Array.isArray(workers) ? workers : []).map((w) => {
          const m = memberMap[w.memberId];
          const firstName = m?.firstName || "";
          const lastName  = m?.lastName  || "";
          const fullName  = locale === "ja"
            ? `${lastName} ${firstName}`.trim()
            : `${firstName} ${lastName}`.trim();
          return {
            ...w,
            memberName: fullName || w.memberName,
            industryId: m?.industryId,
            memberTypeLabel: getMemberTypeLabel(m?.industryId),
          };
        })
      );
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredRows = searchText
    ? rows.filter((r) => r.memberName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "memberName",     headerName: t("member.name"),              flex: 1, minWidth: 160 },
    { field: "memberTypeLabel",headerName: t("member.type"),              width: 80 },
    { field: "departmentName", headerName: t("department.name"),          flex: 1, minWidth: 160 },
    { field: "workerName",     headerName: t("project.member.worker.name"), flex: 1, minWidth: 160 },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{project ? `${project.projectName}・${t("project.member.list.title")}` : t("project.member.list.title")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button variant="contained" onClick={() => navigate(`${basePath}/members/assign`)}>
          {t("project.member.assign.title")}
        </Button>
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.workerId ?? r.memberId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        onRowClick={(params) => navigate(`${basePath}/members/${params.row.memberId}`)}
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
        }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
